import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { AlertTriangle, Wrench, Save, Trash2, Loader2, Bell } from 'lucide-react';
import { extractApiErrorMessage, networkErrorMessage } from '../lib/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Converts a local datetime-local input value to an ISO UTC string.
 * Returns '' if empty.
 */
const COLOR_OPTIONS = [
  { id: 'orange', label: 'Laranja', bg: 'bg-orange-700', text: 'text-orange-50', ring: 'ring-orange-700' },
  { id: 'yellow', label: 'Amarelo', bg: 'bg-yellow-500',  text: 'text-yellow-50',  ring: 'ring-yellow-500'  },
  { id: 'green',  label: 'Verde',   bg: 'bg-green-700',  text: 'text-green-50',  ring: 'ring-green-700'  },
  { id: 'blue',   label: 'Azul',    bg: 'bg-blue-700',   text: 'text-blue-50',   ring: 'ring-blue-700'   },
  { id: 'red',    label: 'Vermelho',bg: 'bg-red-700',    text: 'text-red-50',    ring: 'ring-red-700'    },
];

function localToIso(local) {
  if (!local) return '';
  try {
    return new Date(local).toISOString();
  } catch {
    return '';
  }
}

/**
 * Converts an ISO UTC string to a datetime-local input value (yyyy-MM-ddTHH:mm).
 */
function isoToLocal(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

function fmtBR(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function MaintenancePage() {
  const [loading, setLoading] = useState(true);
  const [savingNotice, setSavingNotice] = useState(false);
  const [savingMode, setSavingMode] = useState(false);

  // Part A — Notice
  const [noticeMessage, setNoticeMessage] = useState('');
  const [showFrom, setShowFrom] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [noticeColor, setNoticeColor] = useState('yellow');
  const [hasSavedNotice, setHasSavedNotice] = useState(false);

  // Part B — Mode
  const [modeMessage, setModeMessage] = useState('Sistema em manutenção. Voltamos em breve.');
  const [modeEndAt, setModeEndAt] = useState('');
  const [savedMode, setSavedMode] = useState({ active: false, activated_at: null, estimated_end_at: null });

  // Live ticker for active mode
  const [elapsed, setElapsed] = useState('');

  // Unified confirm dialog state
  const [confirm, setConfirm] = useState({
    open: false,
    title: '',
    description: '',
    confirmLabel: 'Confirmar',
    variant: 'default', // 'default' | 'destructive'
    onConfirm: null,
  });
  const openConfirm = (cfg) => setConfirm({ open: true, ...cfg });
  const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchAll = async () => {
    try {
      const res = await fetch(`${API_URL}/api/super/maintenance`, { headers: authHeaders() });
      if (!res.ok) {
        toast.error(await extractApiErrorMessage(res, 'Erro ao carregar dados'));
        return;
      }
      const data = await res.json();
      const n = data.notice || {};
      setNoticeMessage(n.message || '');
      setShowFrom(isoToLocal(n.show_from));
      setStartsAt(isoToLocal(n.starts_at));
      setEndsAt(isoToLocal(n.ends_at));
      setNoticeColor(n.color || 'yellow');
      setHasSavedNotice(!!(n.message && n.show_from && n.starts_at && n.ends_at));

      const m = data.mode || {};
      setModeMessage(m.message || 'Sistema em manutenção. Voltamos em breve.');
      setModeEndAt(isoToLocal(m.estimated_end_at));
      setSavedMode({
        active: !!m.active,
        activated_at: m.activated_at || null,
        estimated_end_at: m.estimated_end_at || null,
      });
    } catch (e) {
      toast.error(networkErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Live elapsed timer when maintenance is active
  useEffect(() => {
    if (!savedMode.active || !savedMode.activated_at) {
      setElapsed('');
      return;
    }
    const tick = () => {
      const start = new Date(savedMode.activated_at).getTime();
      const diff = Date.now() - start;
      const totalMin = Math.floor(diff / 60000);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      if (h > 0) setElapsed(`${h}h ${m.toString().padStart(2, '0')}min`);
      else setElapsed(`${m} min`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [savedMode.active, savedMode.activated_at]);

  // --------- Part A handlers ---------
  const handleSaveNotice = async () => {
    if (!noticeMessage.trim()) { toast.error('Informe a mensagem do aviso.'); return; }
    if (!showFrom || !startsAt || !endsAt) { toast.error('Informe todas as datas.'); return; }
    setSavingNotice(true);
    try {
      const res = await fetch(`${API_URL}/api/super/maintenance-notice`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          message: noticeMessage.trim(),
          show_from: localToIso(showFrom),
          starts_at: localToIso(startsAt),
          ends_at: localToIso(endsAt),
          color: noticeColor,
        }),
      });
      if (res.ok) {
        toast.success('Aviso de manutenção salvo.');
        setHasSavedNotice(true);
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao salvar aviso'));
      }
    } catch (e) {
      toast.error(networkErrorMessage(e));
    } finally {
      setSavingNotice(false);
    }
  };

  const handleClearNotice = async () => {
    const colorName = (COLOR_OPTIONS.find((c) => c.id === noticeColor) || COLOR_OPTIONS[1]).label.toLowerCase();
    openConfirm({
      title: 'Remover aviso programado?',
      description: `O banner ${colorName} deixará de aparecer para os usuários. Você poderá criar um novo aviso a qualquer momento.`,
      confirmLabel: 'Remover',
      variant: 'destructive',
      onConfirm: doClearNotice,
    });
  };

  const doClearNotice = async () => {
    closeConfirm();
    setSavingNotice(true);
    try {
      const res = await fetch(`${API_URL}/api/super/maintenance-notice`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ clear: true }),
      });
      if (res.ok) {
        toast.success('Aviso removido.');
        setNoticeMessage(''); setShowFrom(''); setStartsAt(''); setEndsAt('');
        setHasSavedNotice(false);
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao remover'));
      }
    } catch (e) {
      toast.error(networkErrorMessage(e));
    } finally {
      setSavingNotice(false);
    }
  };

  // --------- Part B handlers ---------
  const handleSaveMode = async () => {
    // Persists message/ETA changes while mode is already active (no toggle change).
    setSavingMode(true);
    try {
      const res = await fetch(`${API_URL}/api/super/maintenance-mode`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          active: true,
          message: modeMessage.trim(),
          estimated_end_at: modeEndAt ? localToIso(modeEndAt) : null,
        }),
      });
      if (res.ok) {
        toast.success('Alterações salvas.');
        await fetchAll();
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao salvar alterações'));
      }
    } catch (e) {
      toast.error(networkErrorMessage(e));
    } finally {
      setSavingMode(false);
    }
  };

  const handleToggleMode = async (newActive) => {
    if (newActive) {
      openConfirm({
        title: 'Ativar modo de manutenção agora?',
        description: 'A tela de login será substituída pela tela de manutenção e novos logins serão bloqueados (exceto Super Admin).',
        confirmLabel: 'Ativar manutenção',
        variant: 'destructive',
        onConfirm: () => doToggleMode(true),
      });
      return;
    }
    openConfirm({
      title: 'Desativar modo de manutenção?',
      description: 'O sistema retornará ao funcionamento normal e os logins serão liberados para todos os usuários.',
      confirmLabel: 'Desativar',
      variant: 'default',
      onConfirm: () => doToggleMode(false),
    });
  };

  const doToggleMode = async (newActive) => {
    closeConfirm();
    setSavingMode(true);
    try {
      const res = await fetch(`${API_URL}/api/super/maintenance-mode`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          active: newActive,
          message: modeMessage.trim(),
          estimated_end_at: modeEndAt ? localToIso(modeEndAt) : null,
        }),
      });
      if (res.ok) {
        toast.success(newActive ? 'Modo de manutenção ATIVADO.' : 'Modo de manutenção desativado.');
        await fetchAll();
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao alterar modo'));
      }
    } catch (e) {
      toast.error(networkErrorMessage(e));
    } finally {
      setSavingMode(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Manutenção</h1>
        <p className="text-muted-foreground">
          Avise os usuários antes da manutenção e ative o modo de manutenção quando for a hora.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* CARD A — Aviso programado */}
      <Card className="flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Aviso Programado
          </CardTitle>
          <CardDescription>
            Banner amarelo exibido no topo do sistema para todos os usuários logados, entre as datas configuradas.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="notice-message">Mensagem do aviso</Label>
                <Textarea
                  id="notice-message"
                  value={noticeMessage}
                  onChange={(e) => setNoticeMessage(e.target.value)}
                  placeholder="Ex: Manutenção programada para melhorias no sistema. Salve seus trabalhos com antecedência."
                  rows={3}
                  maxLength={300}
                  data-testid="notice-message-input"
                />
                <p className="text-xs text-muted-foreground">{noticeMessage.length}/300</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="show-from">Mostrar a partir de</Label>
                <Input id="show-from" type="datetime-local" value={showFrom} onChange={(e) => setShowFrom(e.target.value)} data-testid="show-from-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="starts-at">Início da manutenção</Label>
                  <Input id="starts-at" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} data-testid="starts-at-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ends-at">Fim da manutenção</Label>
                  <Input id="ends-at" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} data-testid="ends-at-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Escolher cor do banner de aviso</Label>
                <div className="flex flex-wrap gap-2" data-testid="notice-color-picker">
                  {COLOR_OPTIONS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setNoticeColor(c.id)}
                      title={c.label}
                      aria-label={`Cor ${c.label}`}
                      data-testid={`notice-color-${c.id}`}
                      className={`flex items-center justify-center w-16 h-9 rounded-md border-2 ${c.bg} ${c.text} text-xs font-semibold border-current transition-transform hover:scale-105 ${
                        noticeColor === c.id ? `ring-2 ring-offset-2 ${c.ring}` : 'opacity-80'
                      }`}
                    >
                      Aa
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2">
                {hasSavedNotice && (
                  <Button type="button" variant="outline" onClick={handleClearNotice} disabled={savingNotice} data-testid="clear-notice-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remover Aviso
                  </Button>
                )}
                <Button type="button" onClick={handleSaveNotice} disabled={savingNotice} data-testid="save-notice-btn">
                  {savingNotice ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Salvar Aviso
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* CARD B — Modo de Manutenção */}
      <Card className="flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Modo de Manutenção
          </CardTitle>
          <CardDescription>
            Quando ativado, a tela de login é substituída pela tela de manutenção e novos logins são bloqueados (exceto Super Admin).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 flex-1 flex flex-col">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              {/* Status atual */}
              {savedMode.active && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-1" data-testid="maintenance-active-banner">
                  <div className="flex items-center gap-2 text-red-700 font-semibold">
                    <AlertTriangle className="w-4 h-4" />
                    Modo de manutenção ATIVO
                  </div>
                  <p className="text-sm text-red-700">
                    Ativado em: <strong>{fmtBR(savedMode.activated_at)}</strong>
                  </p>
                  {savedMode.estimated_end_at && (
                    <p className="text-sm text-red-700">
                      Previsto fim: <strong>{fmtBR(savedMode.estimated_end_at)}</strong>
                    </p>
                  )}
                  {elapsed && (
                    <p className="text-sm text-red-700">Tempo decorrido: <strong>{elapsed}</strong></p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between p-4 border bg-secondary/30 rounded-lg">
                <div className="space-y-0.5 pr-3">
                  <Label htmlFor="mode-toggle" className="font-medium">
                    Ativar modo de manutenção
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Ao ligar, novos logins serão bloqueados imediatamente.
                  </p>
                </div>
                <Switch
                  id="mode-toggle"
                  checked={savedMode.active}
                  onCheckedChange={(checked) => handleToggleMode(checked)}
                  disabled={savingMode}
                  data-testid="mode-toggle-switch"
                />
              </div>

              <div className="space-y-2 flex-1 flex flex-col">
                <Label htmlFor="mode-message">Mensagem na tela de manutenção (opcional)</Label>
                <Textarea
                  id="mode-message"
                  value={modeMessage}
                  onChange={(e) => setModeMessage(e.target.value)}
                  placeholder="Sistema em manutenção. Voltamos em breve."
                  maxLength={300}
                  className="flex-1 min-h-[80px] resize-none"
                  data-testid="mode-message-input"
                />
                <p className="text-xs text-muted-foreground">{modeMessage.length}/300</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mode-end-at">Previsão de retorno (opcional)</Label>
                <Input
                  id="mode-end-at"
                  type="datetime-local"
                  value={modeEndAt}
                  onChange={(e) => setModeEndAt(e.target.value)}
                  data-testid="mode-end-at-input"
                />
                <p className="text-xs text-muted-foreground">
                  Aparece na tela pública como referência ao usuário. Não desativa automaticamente.
                </p>
              </div>

              {savingMode && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Atualizando...
                </div>
              )}

              {savedMode.active && (
                <div className="flex justify-end pt-2">
                  <Button
                    type="button"
                    onClick={handleSaveMode}
                    disabled={savingMode}
                    data-testid="save-mode-btn"
                  >
                    {savingMode
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Save className="w-4 h-4 mr-2" />}
                    Salvar alterações
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Unified confirm dialog (replaces window.confirm) */}
      <Dialog open={confirm.open} onOpenChange={(o) => { if (!o) closeConfirm(); }}>
        <DialogContent className="max-w-md" data-testid="maintenance-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirm.variant === 'destructive' && (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              )}
              {confirm.title}
            </DialogTitle>
            <DialogDescription className="pt-1 leading-relaxed">
              {confirm.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={closeConfirm}
              data-testid="confirm-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant={confirm.variant === 'destructive' ? 'destructive' : 'default'}
              onClick={() => { if (confirm.onConfirm) confirm.onConfirm(); }}
              data-testid="confirm-ok-btn"
            >
              {confirm.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
